import { createContext, ReactNode, useContext } from "react";

export interface DesignTokens {
  colors: {
    bg: string;
    bg1: string;
    bg2: string;
    bg3: string;
    cyan: string;
    cyanDim: string;
    cyanGlow: string;
    green: string;
    red: string;
    blue: string;
    text: string;
    textDim: string;
    textMuted: string;
    border: string;
    borderBright: string;
  };
  fonts: {
    mono: string;
    display: string;
  };
  animations: {
    fadeIn: string;
    pulse: string;
  };
}

export const sentinelTokens: DesignTokens = {
  colors: {
    bg: "#111111",
    bg1: "#111111",
    bg2: "#1a1a1a",
    bg3: "#222222",
    cyan: "#00b8db",
    cyanDim: "#0088a3",
    cyanGlow: "rgba(0, 184, 219, 0.12)",
    green: "#00e5a0",
    red: "#ff4757",
    blue: "#3d9eff",
    text: "#e0e0e0",
    textDim: "#a0a0a0",
    textMuted: "#555555",
    border: "rgba(255, 255, 255, 0.08)",
    borderBright: "rgba(0, 184, 219, 0.35)",
  },
  fonts: {
    mono: "var(--font-mono)",
    display: "var(--font-display)",
  },
  animations: {
    fadeIn: "fadein 0.4s ease both",
    pulse: "pulse 2s infinite",
  },
};

interface DesignSystemContextType {
  tokens: DesignTokens;
}

const DesignSystemContext = createContext<DesignSystemContextType | undefined>(
  undefined
);

interface DesignSystemProviderProps {
  children: ReactNode;
  className?: string;
}

export function DesignSystemProvider({
  children,
  className,
}: DesignSystemProviderProps) {
  const value = {
    tokens: sentinelTokens,
  };

  return (
    <DesignSystemContext.Provider value={value}>
      <div
        className={className}
        style={{
          fontFamily: "var(--font-sans)",
          color: sentinelTokens.colors.text,
        }}
      >
        {children}
      </div>
    </DesignSystemContext.Provider>
  );
}

export function useDesignSystem() {
  const context = useContext(DesignSystemContext);
  if (context === undefined) {
    throw new Error(
      "useDesignSystem must be used within a DesignSystemProvider"
    );
  }
  return context;
}

export { DesignSystemContext };
